import React from 'react';
import { Pane } from '@ui/molecules/Pane';
import { Status } from '@ui/atoms/Status';
import { Text } from '@ui';
import { translate } from '@utils/i18n';
import useGetIntegrationStatus from '@hooks/requests/integration-status.get.hook';
import { SyncStatusRow } from '../SyncStatusRow';
import { SyncErrorBanner } from '../SyncErrorBanner';
import { SyncHealthBadge } from '../SyncHealthBadge';
import { formatRelativeTime } from '../utils/formatRelativeTime';
const NAMESPACE = 'features.settings.integrations.payrix_payments_status_panel';
/**
 * Page-level wrapper island for the Payrix Payments Status panel.
 * Mounts via react_component('Settings/Integrations/PayrixPaymentsStatusPanel').
 *
 * Has a static "Merchant account: Configured" row (page only renders when configured),
 * and derives a "Last processing error" row from payment_events.last_error_*.
 */
function PayrixPaymentsStatusPanel() {
    var _a;
    const { data, loading, error } = useGetIntegrationStatus('payrix');
    const title = translate('title', { namespace: NAMESPACE });
    const subtitle = translate('subtitle', { namespace: NAMESPACE });
    const footerNote = translate('footer_note', { namespace: NAMESPACE });
    // Don't fall through to a "Connected" header when the status fetch failed
    // with no data — show the loading/unknown state instead.
    if ((loading || error) && !data) {
        return (React.createElement(Pane, { title: title, subtitle: subtitle },
            React.createElement("div", { style: { padding: '20px' } },
                React.createElement("i", { className: "fa-light fa-circle-notch fa-spin", "aria-hidden": "true" }))));
    }
    const paymentEventsCategory = ((_a = data === null || data === void 0 ? void 0 : data.categories) !== null && _a !== void 0 ? _a : []).find((c) => c.category === 'payment_events');
    // Derive "Last processing error" from payment_events error fields
    const hasError = (paymentEventsCategory === null || paymentEventsCategory === void 0 ? void 0 : paymentEventsCategory.lastErrorAt) &&
        (!paymentEventsCategory.lastSuccessAt ||
            new Date(paymentEventsCategory.lastErrorAt) > new Date(paymentEventsCategory.lastSuccessAt));
    const headerLabel = hasError
        ? translate('attention_needed', { namespace: NAMESPACE })
        : translate('connected_label', { namespace: NAMESPACE });
    const footer = (React.createElement(Text, { variant: "x-small", style: { color: 'var(--gray-400)' } }, footerNote));
    return (React.createElement(Pane, { title: title, subtitle: subtitle, headerRight: React.createElement(SyncHealthBadge, { state: hasError ? 'attention' : 'healthy', label: headerLabel }), footer: footer },
        hasError && paymentEventsCategory && React.createElement(SyncErrorBanner, { errorCategory: paymentEventsCategory }),
        React.createElement("div", { className: "qmb-ui-sync-status-row" },
            React.createElement("div", { className: "sync-status-row__label-cell" },
                React.createElement(Text, { variant: "body", className: "sync-status-row__label" }, translate('row_merchant_account', { namespace: NAMESPACE }))),
            React.createElement("div", { className: "sync-status-row__status-cell" },
                React.createElement(Status, { label: translate('merchant_configured', { namespace: NAMESPACE }), color: "green-300" }))),
        paymentEventsCategory && (React.createElement(SyncStatusRow, { label: translate('row_payment_events', { namespace: NAMESPACE }), category: paymentEventsCategory, syncable: false, timestampVerb: "received", subject: paymentEventsCategory.subject })),
        React.createElement("div", { className: "qmb-ui-sync-status-row" },
            React.createElement("div", { className: "sync-status-row__label-cell" },
                React.createElement(Text, { variant: "body", className: "sync-status-row__label" }, translate('row_last_processing_error', { namespace: NAMESPACE }))),
            React.createElement("div", { className: "sync-status-row__status-cell" }, buildProcessingErrorStatus(paymentEventsCategory, NAMESPACE)))));
}
function buildProcessingErrorStatus(paymentEventsCategory, ns) {
    var _a;
    if (!(paymentEventsCategory === null || paymentEventsCategory === void 0 ? void 0 : paymentEventsCategory.lastErrorAt)) {
        return React.createElement(Status, { label: translate('no_error_recorded', { namespace: ns }), color: "gray-500" });
    }
    const relativeTime = formatRelativeTime(paymentEventsCategory.lastErrorAt, translate('today', { namespace: ns }));
    const label = translate('last_error_at', { namespace: ns, time: relativeTime });
    return React.createElement(Status, { label: label, color: "red-500", tooltip: (_a = paymentEventsCategory.lastErrorMessage) !== null && _a !== void 0 ? _a : undefined });
}
export default PayrixPaymentsStatusPanel;
